import { action, observable, computed } from 'mobx';
import { Site as SiteSchemaType, CurrencyCurrency } from '@linktivity/ars-neo';
import { LanguageCodeType } from '@link/share/services/types';
import siteApi from '@link/skytree/services/endpoints/site';

const storage = window.sessionStorage;

export class Site {
  @observable accessor siteSchema: SiteSchemaType = {};
  @observable accessor siteLoading: boolean = false;
  // Flips to true when the first schema fetch settles (success or failure);
  // the profile gates use it to tell "fetch failed" from "not fetched yet".
  @observable accessor siteSettled: boolean = false;
  @observable accessor _currency: CurrencyCurrency | null =
    (storage.getItem('currency') as CurrencyCurrency) || null;

  async getSiteSchema(languageCode: LanguageCodeType) {
    this.setSiteLoading(true);
    try {
      const res = await siteApi.getSiteSchema({ languageCode });
      const site = res?.site;
      if (site) {
        this.setSiteSchema(site);
      }
      return site;
    } finally {
      this.setSiteLoading(false);
      this.setSiteSettled(true);
    }
  }

  @action
  setSiteLoading(siteLoading: boolean) {
    this.siteLoading = siteLoading;
  }

  @action
  setSiteSettled(siteSettled: boolean) {
    this.siteSettled = siteSettled;
  }

  @action
  setSiteSchema(siteSchema?: SiteSchemaType) {
    if (siteSchema) {
      this.siteSchema = siteSchema;
    }
  }

  getSite() {
    return this.siteSchema;
  }

  get currency() {
    return (
      this._currency ||
      this.siteSchema?.siteLayout?.currency ||
      CurrencyCurrency.Jpy
    );
  }

  @action
  setCurrency(currency: CurrencyCurrency) {
    this._currency = currency;
    storage.setItem('currency', currency);
  }

  @computed
  get supportedLanguageCodes() {
    return (
      this.siteSchema?.siteLayout?.headerConfig?.supportedLanguageCodes || []
    );
  }

  @computed
  get supportedCurrencies() {
    return this.siteSchema?.siteLayout?.headerConfig?.supportedCurrencies || [];
  }

  @computed
  get logoUrl() {
    return this.siteSchema?.siteLayout?.headerConfig?.logoUrl;
  }

  @computed
  get showOtherCurrencies() {
    return (
      this.siteSchema?.siteLayout?.headerConfig?.showOtherCurrencies ?? false
    );
  }

  @computed
  get userProfileForm() {
    return this.siteSchema?.siteLayout?.authConfig?.userProfileForm;
  }
}

export default new Site();
